import { Skeleton } from "@/components/ui/skeleton";
import { useForm } from "@/features/qualifications/hooks/use-qualification-forms";
import { FileTextIcon } from "@phosphor-icons/react";
import { useTranslation } from "@/features/utils/i18n";

interface FormTileProps {
  formId: string;
  fallback?: string;
  showIcon?: boolean;
  showDescription?: boolean;
  showQuestionCount?: boolean;
}

export function FormTile({
  formId,
  fallback,
  showIcon = true,
  showDescription = false,
  showQuestionCount = true,
}: FormTileProps) {
  const { t } = useTranslation();
  fallback ??= t("common.unknownForm");
  const { data: form, isLoading } = useForm(formId);

  if (!formId) {
    return <span className="text-muted-foreground">{fallback}</span>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        {showIcon && <Skeleton className="h-4 w-4 rounded" />}
        <div className="space-y-1">
          <Skeleton className="h-4 w-32" />
          {(showDescription || showQuestionCount) && (
            <Skeleton className="h-3 w-24" />
          )}
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex items-center gap-2">
        {showIcon && <FileTextIcon className="h-4 w-4 text-muted-foreground" />}
        <span className="text-muted-foreground">{fallback}</span>
      </div>
    );
  }

  const questionCount = form.questions?.questions?.length || 0;

  return (
    <div className="flex items-center gap-2">
      {showIcon && (
        <FileTextIcon
          className="h-4 w-4 text-primary shrink-0"
          weight="duotone"
        />
      )}
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-sm">{form.name}</span>
        {showDescription && form.description && (
          <span className="text-xs text-muted-foreground line-clamp-1">
            {form.description}
          </span>
        )}
        {showQuestionCount && (
          <span className="text-xs text-muted-foreground">
            {t("common.questionCount", { count: questionCount })}
          </span>
        )}
      </div>
    </div>
  );
}
